import { RequireAuth, useFusionAuth } from "@fusionauth/react-sdk";
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { deviceApiPrefix, sourceAsText } from "../../../utils/main.ts";

const statuses = [
  {
    name: "Preview Mode",
    value: 0,
  },
  {
    name: "In Progress",
    value: 1,
  },
  {
    name: "Completed",
    value: 2,
  },
];
export default function Project({
  displayContent,
  displayErrors,
  onUpdateData,
  projectData,
  createProjectError,
}: {
  displayContent: boolean;
  displayErrors: boolean;
  onUpdateData: (data: Partial<ProjectContextType>) => void;
  projectData: ProjectContextType;
  createProjectError: unknown;
}) {
  const showNameError = displayErrors && projectData.name.trim() === "";
  const showDatasourceError = displayErrors && !projectData.sourceId;
  const { data: validSources, fetch: getSources } = useFetch<
    Source[],
    Source
  >();
  const [dataSource, setDataSource] = useState("");

  const { userInfo } = useFusionAuth();
  const isAdmin = userInfo?.roles?.includes("ADMIN");

  useEffect(() => {
    if (isAdmin) {
      void getSources(`${deviceApiPrefix}/sources/`);
    }
  }, [getSources, isAdmin]);

  function onSourceChange(event: SelectChangeEvent) {
    if (!validSources) return;
    const selectedSources = validSources.filter(
      (source: Source) => source.id === event.target.value,
    );
    if (selectedSources.length > 0) {
      setDataSource((selectedSources[0] && selectedSources[0].id) || "");
      onUpdateData({ sourceId: selectedSources[0] && selectedSources[0].id });
    } else {
      setDataSource("");
      onUpdateData({ sourceId: undefined });
    }
  }

  if (!displayContent) {
    return null;
  }
  return (
    <Box className="project-details-container" sx={{ bgcolor: "grey.50" }}>
      <Typography variant={"body2"} sx={{ marginBottom: "5px" }}>
        As a final step, provide a name and description for this project. This
        information can be modified at any time after the project is created.
      </Typography>
      <Typography
        variant={"body1"}
        sx={{ marginBottom: "10px", color: "text.secondary" }}
      >
        * Required fields
      </Typography>
      <FormControl fullWidth variant="standard">
        <TextField
          label="Project Name"
          variant="outlined"
          error={showNameError}
          slotProps={{
            input: { sx: { fontSize: 14 } },
          }}
          size="medium"
          sx={{ bgcolor: "common.white" }}
          value={projectData.name}
          onChange={(event) => onUpdateData({ name: event.target.value })}
          required
        />
        {showNameError && (
          <FormHelperText error>This field is required</FormHelperText>
        )}
      </FormControl>
      <FormControl fullWidth variant="standard">
        <TextField
          label="Project Description"
          variant="outlined"
          slotProps={{
            input: { sx: { fontSize: 14 } },
          }}
          size="medium"
          sx={{ bgcolor: "common.white" }}
          value={projectData.description}
          onChange={(event) =>
            onUpdateData({ description: event.target.value })
          }
        />
      </FormControl>
      <RequireAuth withRole="ADMIN">
        <FormControl fullWidth>
          <InputLabel
            id="select-source-label"
            required
            error={showDatasourceError}
          >
            Data Source
          </InputLabel>
          <Select
            required
            label={"Data Source"}
            labelId="select-source-label"
            sx={{ bgcolor: "common.white" }}
            onChange={onSourceChange}
            value={dataSource}
            inputProps={{ id: "select-source", sx: { fontSize: 14 } }}
            variant="outlined"
            error={showDatasourceError}
          >
            <MenuItem value="">None</MenuItem>
            {validSources &&
              validSources.map((source) => {
                return (
                  <MenuItem
                    key={`${source.name}_${source.databaseName}`}
                    value={source.id}
                  >
                    {sourceAsText(source)}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </RequireAuth>
      <RequireAuth withRole="ADMIN">
        <FormControl fullWidth>
          <InputLabel id="select-status-label" required>
            Status
          </InputLabel>
          <Select
            required
            label={"Status"}
            labelId="select-status-label"
            sx={{ bgcolor: "common.white" }}
            onChange={(event) => onUpdateData({ status: event.target.value })}
            value={projectData.status || 0}
            inputProps={{ id: "select-status", sx: { fontSize: 14 } }}
            variant="outlined"
          >
            {statuses.map((value) => {
              return (
                <MenuItem key={value.value} value={value.value}>
                  {value.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </RequireAuth>
      {typeof createProjectError === "object" &&
        createProjectError &&
        "message" in createProjectError &&
        displayErrors && (
          <Alert severity="error">{String(createProjectError.message)}</Alert>
        )}
    </Box>
  );
}
